import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Share2, Download } from "lucide-react";
import { fmt } from "../lib/format";
import { startBalance, milestoneTimeline, goalTimeline } from "../lib/finance";
import { Button } from "./ui/button";
import { Switch } from "./ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";

const css = (v, fb) => {
  const raw = getComputedStyle(document.documentElement).getPropertyValue(v).trim();
  return raw ? `hsl(${raw})` : fb;
};
const longDate = (s) => (s ? new Date(`${s}T00:00:00`).toLocaleDateString(undefined, { month: "long", day: "numeric", year: "numeric" }) : "");

// Latest reached milestone (or payoff) for a debt
export function shareInfo(debt) {
  const reached = milestoneTimeline(debt).filter((m) => m.reached);
  const top = reached[reached.length - 1];
  if (!top) return null;
  const start = startBalance(debt);
  const done = top.pct === 100;
  const saved = done && !debt.paidOff?.neverPaid ? debt.paidOff?.interestSaved : null;
  const early = done && debt.paidOff?.monthsEarly ? `${debt.paidOff.monthsEarly} months early` : null;
  return {
    name: debt.creditor, pct: top.pct, date: top.date, file: debt.creditor,
    label: done ? "PAID OFF" : "PAID DOWN",
    title: done ? `${debt.creditor} is gone!` : `${top.pct}% of ${debt.creditor} paid`,
    amountLines: [!done && `${fmt(start - Number(debt.balance))} paid of ${fmt(start)}`, saved && `≈ ${fmt(saved)} interest avoided${early ? ` · ${early}` : ""}`].filter(Boolean),
    plainLines: [early].filter(Boolean),
    footer: "debt-free journey",
  };
}

export function goalShareInfo(goal) {
  const reached = goalTimeline(goal).filter((m) => m.reached);
  const top = reached[reached.length - 1];
  if (!top) return null;
  const done = top.pct === 100;
  return {
    name: goal.name, pct: top.pct, date: top.date, file: goal.name,
    label: done ? "FULLY SAVED" : "SAVED",
    title: done ? `${goal.name} fully funded!` : `${top.pct}% of ${goal.name} saved`,
    amountLines: [`${fmt(goal.current)} saved of ${fmt(goal.target)}`],
    plainLines: [],
    footer: "savings goals",
  };
}

function drawCard(info, hideAmounts) {
  const S = 1080;
  const c = document.createElement("canvas");
  c.width = S;
  c.height = S;
  const g = c.getContext("2d");
  const font = getComputedStyle(document.body).fontFamily || "sans-serif";
  const income = css("--income", "#0d9488");
  const bg = g.createLinearGradient(0, 0, S, S);
  bg.addColorStop(0, "#0f172a");
  bg.addColorStop(1, "#134e4a");
  g.fillStyle = bg;
  g.fillRect(0, 0, S, S);
  for (let i = 0; i < 26; i++) {
    g.fillStyle = ["#2dd4bf", "#fbbf24", "#f472b6", "#60a5fa", "#a3e635"][i % 5];
    g.globalAlpha = 0.55;
    g.fillRect((i * 173) % S, (i * 97) % 170 + 20, 14, 22);
  }
  g.globalAlpha = 1;
  const cx = S / 2, cy = 420, r = 190;
  g.lineWidth = 34;
  g.lineCap = "round";
  g.strokeStyle = "rgba(255,255,255,.12)";
  g.beginPath(); g.arc(cx, cy, r, 0, Math.PI * 2); g.stroke();
  g.strokeStyle = income;
  g.beginPath(); g.arc(cx, cy, r, -Math.PI / 2, -Math.PI / 2 + (Math.PI * 2 * info.pct) / 100); g.stroke();
  g.fillStyle = "#fff";
  g.textAlign = "center";
  g.font = `800 120px ${font}`;
  g.fillText(`${info.pct}%`, cx, cy + 40);
  g.font = `600 30px ${font}`;
  g.fillStyle = "rgba(255,255,255,.7)";
  g.fillText(info.label, cx, cy + 90);
  g.fillStyle = "#fff";
  g.font = `800 64px ${font}`;
  g.fillText(info.title, cx, 740, S - 120);
  g.font = `500 34px ${font}`;
  g.fillStyle = "rgba(255,255,255,.8)";
  const lines = [];
  if (info.date) lines.push(`Reached ${longDate(info.date)}`);
  lines.push(...(hideAmounts ? info.plainLines : info.amountLines));
  lines.forEach((l, i) => g.fillText(l, cx, 810 + i * 52, S - 120));
  g.font = `600 26px ${font}`;
  g.fillStyle = "rgba(255,255,255,.5)";
  g.fillText(`Household Budget · ${info.footer}`, cx, S - 50);
  return c;
}

export function ShareDialog({ info, onClose }) {
  const [hide, setHide] = useState(true);
  const [url, setUrl] = useState(null);
  const [blob, setBlob] = useState(null);
  useEffect(() => {
    let u;
    drawCard(info, hide).toBlob((b) => { setBlob(b); setUrl((u = URL.createObjectURL(b))); }, "image/png");
    return () => u && URL.revokeObjectURL(u);
  }, [info, hide]);
  const name = `${info.file.replace(/[^\w]+/g, "-").toLowerCase()}-${info.pct}pct.png`;
  const file = blob && new File([blob], name, { type: "image/png" });
  const canShare = !!(file && navigator.canShare?.({ files: [file] }));
  const share = async () => {
    try { await navigator.share({ files: [file], title: info.title }); } catch (e) { if (e.name !== "AbortError") toast.error("Sharing isn't available — use Save image"); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="share-card-dialog">
        <DialogHeader>
          <DialogTitle>Share your progress</DialogTitle>
          <DialogDescription>The image is made on this device. Amounts are hidden unless you turn them on.</DialogDescription>
        </DialogHeader>
        {url && <img src={url} alt="Milestone card" className="w-full rounded-2xl border" data-testid="share-card-image" />}
        <label className="flex items-center gap-2 text-sm"><Switch checked={hide} onCheckedChange={setHide} data-testid="share-hide-amounts-switch" /> Hide amounts</label>
        <div className="grid grid-cols-2 gap-2">
          <Button variant="outline" asChild disabled={!url}><a href={url || undefined} download={name} data-testid="share-download-button"><Download className="h-4 w-4 mr-1" /> Save image</a></Button>
          <Button onClick={share} disabled={!canShare} title={canShare ? "" : "Your browser can't share images directly — save it instead"} data-testid="share-native-button"><Share2 className="h-4 w-4 mr-1" /> Share</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function ShareButton({ debt, goal, className }) {
  const [open, setOpen] = useState(false);
  const info = goal ? goalShareInfo(goal) : shareInfo(debt);
  if (!info) return null;
  return (
    <>
      <Button variant="ghost" size="sm" className={className} onClick={() => setOpen(true)} data-testid={goal ? `share-goal-${goal.id}` : `share-debt-${debt.id}`}><Share2 className="h-3.5 w-3.5 mr-1" /> Share</Button>
      {open && <ShareDialog info={info} onClose={() => setOpen(false)} />}
    </>
  );
}
